import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormState } from '@shared';
import {
  AccountCategory,
  AccountCategoryCode,
} from '@account-users/account/models/AccountCategory';
import { AccountCreateRequest } from '@account-users/account/models/AccountCreateRequest';
import {
  AllInOneQuotas,
  FreemiumQuotas,
  LicensePackage,
  LicensePackageCode,
  LiteQuotas,
  EssentialQuotas,
  TrialQuotas,
  SendQuotas,
  AutomateQuotas,
  GrowQuotas,
} from '@account-users/account/models/License';
import { Subject } from 'rxjs';

import { customAlphabet } from 'nanoid';
import { takeUntil } from 'rxjs/operators';
import { Permission } from '@account-users/account/models';

// RFC 5322 sender ("from") domain — at least two dot-separated labels, where
// each label starts and ends with alphanumeric (no leading/trailing hyphens,
// no empty labels), each label is 1-63 chars, the TLD is 2-63 letters, and
// the total length is ≤253 chars (DNS spec). An empty (or all-whitespace)
// value is treated as valid here so the FormArray-level "at-least-one"
// validator owns the required check, and any surrounding whitespace is
// forgiven and stripped in onSubmit.
function validRfc5322FromDomain(
  control: AbstractControl,
): ValidationErrors | null {
  const raw = control.value;
  if (raw == null) {
    return null;
  }
  const value = typeof raw === 'string' ? raw.trim() : raw;
  if (value === '') {
    return null;
  }
  const regex = /^(?=.{1,253}$)(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,63}$/u;
  return regex.test(value) ? null : { invalidDomainFormat: true };
}

function atLeastOneFromDomainUnless(isExempt: () => boolean): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (isExempt()) {
      return null;
    }
    const arr = control as FormArray;
    const hasNonEmpty = arr.controls.some(
      (c) => typeof c.value === 'string' && c.value.trim() !== '',
    );
    return hasNonEmpty ? null : { atLeastOneRequired: true };
  };
}

function validRfc5322FromDomainUnless(isExempt: () => boolean): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (isExempt()) {
      return null;
    }
    return validRfc5322FromDomain(control);
  };
}

@Component({
  selector: 'app-create-account-form',
  templateUrl: './create-account-form.component.html',
  styleUrls: ['./create-account-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateAccountFormComponent implements OnChanges, OnDestroy {
  @Input() state: FormState;
  @Input() public set licensePackages(licensePackages: LicensePackage[]) {
    this._licensePackages = licensePackages;
  }
  @Input() isLoadingPackages: boolean;
  @Input() licensePackagesError: boolean;
  @Input() accountCategories: AccountCategory[] = [];
  @Input() userPermissions: Permission[] = [];

  @Output() createAccount = new EventEmitter<AccountCreateRequest>();

  public form: FormGroup;
  public submitted: boolean;

  public licensePackage = LicensePackageCode.Automate;

  public formStates = FormState;
  public AccountCategoryCode = AccountCategoryCode;

  private _destroy$ = new Subject<void>();

  private readonly _alphabet = '1234567890abcdefghijklmnopqrstuvwxyz';
  private readonly _defaultProfileCount = EssentialQuotas.AudienceProfiles;
  private _licensePackages: (LicensePackage & { disabled?: boolean })[] = [];
  private _hasSubscriptionId = false;

  public get hasSubscriptionId() {
    return this._hasSubscriptionId;
  }

  public get licensePackages() {
    return this._licensePackages;
  }

  public get accountId() {
    return this.form.get('accountId') as FormControl;
  }

  public get accountDisplayName() {
    return this.form.get('accountDisplayName') as FormControl;
  }

  public get accountCategory() {
    return this.form.get('accountCategory') as FormControl;
  }

  public get accountDescription() {
    return this.form.get('accountDescription') as FormControl;
  }

  public get orderNumber() {
    return this.form.get('orderNumber') as FormControl;
  }

  public get packageSize() {
    return this.form.get('packageSize') as FormControl;
  }

  public get adminEmail() {
    return this.form.get('adminEmail') as FormControl;
  }

  public get subscriptionId() {
    return this.form.get('subscriptionId') as FormControl;
  }

  public get fromDomains() {
    return this.form.get('fromDomains') as FormArray;
  }

  public get isTrialCategory() {
    return (
      this.form?.get('accountCategory')?.value === AccountCategoryCode.Trial
    );
  }

  get hasWawBackOffice() {
    return this.userPermissions.includes(Permission.WawBackOfficePreLaunch);
  }

  constructor(public activeModal: NgbActiveModal, private fb: FormBuilder) {
    const uuid = customAlphabet(this._alphabet, 16);
    this.form = this.fb.group({
      accountId: [uuid()],
      accountDisplayName: [
        '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(32),
        ],
      ],
      accountCategory: [
        '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(32),
        ],
      ],
      accountDescription: ['', [Validators.maxLength(1000)]],
      subscriptionId: ['', [Validators.pattern(/^[A-Za-z0-9]{16}$/)]],
      packageSize: [
        this._defaultProfileCount,
        [Validators.required, Validators.min(0), Validators.max(9999999)],
      ],
      orderNumber: [
        '',
        [Validators.required, Validators.pattern(/^[A-Za-z0-9]{16}$/)],
      ],
      adminEmail: [
        '',
        [Validators.required, Validators.email, Validators.maxLength(255)],
      ],
      fromDomains: this.fb.array(
        [this.createFromDomainControl()],
        atLeastOneFromDomainUnless(
          () =>
            this.form?.get('accountCategory')?.value ===
            AccountCategoryCode.Trial,
        ),
      ),
    });

    this.accountCategory.valueChanges
      .pipe(takeUntil(this._destroy$))
      .subscribe((ac) => {
        this.updateLicensePackagesByCategory(ac as AccountCategoryCode);
        this.updateSubscriptionIdByCategory(ac as AccountCategoryCode);
        this.revalidateFromDomains();
      });
  }

  private createFromDomainControl(): FormControl {
    return this.fb.control('', [
      validRfc5322FromDomainUnless(() => this.isTrialCategory),
    ]) as FormControl;
  }

  private revalidateFromDomains(): void {
    this.fromDomains.controls.forEach((c) =>
      c.updateValueAndValidity({ emitEvent: false }),
    );
    this.fromDomains.updateValueAndValidity();
  }

  public addFromDomain(): void {
    this.fromDomains.push(this.createFromDomainControl());
  }

  public removeFromDomain(index: number): void {
    if (this.fromDomains.length <= 1) {
      this.fromDomains.at(0).reset('');
      return;
    }
    this.fromDomains.removeAt(index);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const accountCategories = changes['accountCategories'];
    if (accountCategories?.currentValue?.length) {
      this.accountCategory.setValue(accountCategories.currentValue[0].code, {
        emitEvent: false,
      });
      this.updateLicensePackagesByCategory(this.accountCategory.value);
      this.updateSubscriptionIdByCategory(this.accountCategory.value);
      this.revalidateFromDomains();
    }
  }

  public onSubmit(event: Event) {
    event.preventDefault();
    this.state = FormState.submitting;
    this.submitted = true;

    let emailQuota = 0;
    let allowedSeats = 0;
    let smsCredits = 0;

    switch (this.licensePackage) {
      case LicensePackageCode.Lite:
        emailQuota = LiteQuotas.EmailQuota;
        allowedSeats = LiteQuotas.UserSeats;
        smsCredits = LiteQuotas.SmsCredits;
        break;
      case LicensePackageCode.Send:
        emailQuota = SendQuotas.EmailQuota;
        allowedSeats = SendQuotas.UserSeats;
        smsCredits = SendQuotas.SmsCredits;
        break;
      case LicensePackageCode.Essential:
        emailQuota = EssentialQuotas.EmailQuota;
        allowedSeats = EssentialQuotas.UserSeats;
        smsCredits = EssentialQuotas.SmsCredits;
        break;
      case LicensePackageCode.Automate:
        emailQuota = AutomateQuotas.EmailQuota;
        allowedSeats = AutomateQuotas.UserSeats;
        smsCredits = AutomateQuotas.SmsCredits;
        break;
      case LicensePackageCode.AllInOne:
        emailQuota = AllInOneQuotas.EmailQuota;
        allowedSeats = AllInOneQuotas.UserSeats;
        smsCredits = AllInOneQuotas.SmsCredits;
        break;
      case LicensePackageCode.Grow:
        emailQuota = GrowQuotas.EmailQuota;
        allowedSeats = GrowQuotas.UserSeats;
        smsCredits = GrowQuotas.SmsCredits;
        break;
      case LicensePackageCode.Trial:
        emailQuota = TrialQuotas.EmailQuota;
        allowedSeats = TrialQuotas.UserSeats;
        smsCredits = TrialQuotas.SmsCredits;
        break;
      case LicensePackageCode.Freemium:
        emailQuota = FreemiumQuotas.EmailQuota;
        allowedSeats = FreemiumQuotas.UserSeats;
        smsCredits = FreemiumQuotas.SmsCredits;
        break;
    }

    if (this.form.valid) {
      const {
        accountId,
        accountDisplayName,
        accountCategory,
        accountDescription,
        packageSize,
        orderNumber,
        subscriptionId,
        adminEmail,
      } = this.form.value;

      const fromDomains = (this.fromDomains.value as string[])
        .map((d) => (d ?? '').trim())
        .filter((d) => d !== '');

      const account = {
        id: accountId,
        displayName: accountDisplayName,
        category: accountCategory,
        description: accountDescription,
        audiencePackageSize: packageSize,
        orderNumber: orderNumber,
        sbmRef: subscriptionId,
        userSeats: allowedSeats,
        adminEmail: adminEmail.toLowerCase(),
        licensePackage: this.licensePackage,
        emailQuota,
        smsCredits,
        fromDomains,
      };

      this.createAccount.emit(account);
    }
  }

  public clickPackage(licensePackage: LicensePackageCode) {
    if (this.licensePackage === licensePackage) {
      return;
    }
    this.licensePackage = licensePackage;
    let audienceProfiles = 0;
    switch (this.licensePackage) {
      case LicensePackageCode.Lite:
        audienceProfiles = LiteQuotas.AudienceProfiles;
        break;
      case LicensePackageCode.Send:
        audienceProfiles = SendQuotas.AudienceProfiles;
        break;
      case LicensePackageCode.Essential:
        audienceProfiles = EssentialQuotas.AudienceProfiles;
        break;
      case LicensePackageCode.Automate:
        audienceProfiles = AutomateQuotas.AudienceProfiles;
        break;
      case LicensePackageCode.AllInOne:
        audienceProfiles = AllInOneQuotas.AudienceProfiles;
        break;
      case LicensePackageCode.Grow:
        audienceProfiles = GrowQuotas.AudienceProfiles;
        break;
      case LicensePackageCode.Freemium:
        audienceProfiles = FreemiumQuotas.AudienceProfiles;
        break;
    }
    this.packageSize.patchValue(audienceProfiles);
    this.packageSize.setValidators([
      Validators.required,
      Validators.min(0),
      Validators.max(9999999),
    ]);
    this.packageSize.updateValueAndValidity();
  }

  private updateSubscriptionIdByCategory(categoryCode: AccountCategoryCode) {
    this._hasSubscriptionId =
      categoryCode === AccountCategoryCode.External ||
      categoryCode === AccountCategoryCode.Trial ||
      categoryCode === AccountCategoryCode.SuiteMaxo;

    if (this._hasSubscriptionId) {
      const validators =
        categoryCode === AccountCategoryCode.Trial
          ? [Validators.pattern(/^[A-Za-z0-9]{16}$/)]
          : [Validators.required, Validators.pattern(/^[A-Za-z0-9]{16}$/)];
      this.subscriptionId.setValidators(validators);
    } else {
      this.subscriptionId.reset();
      this.subscriptionId.clearValidators();
    }

    this.subscriptionId.updateValueAndValidity();
  }

  private preventCreatingAccountWithLicensePackage(
    cat: AccountCategoryCode,
    pkg: LicensePackageCode,
  ): boolean {
    return (
      (cat === AccountCategoryCode.Trial
        ? pkg !== LicensePackageCode.Trial
        : pkg === LicensePackageCode.Trial) ||
      pkg === LicensePackageCode.AllInOne ||
      pkg === LicensePackageCode.Essential ||
      pkg === LicensePackageCode.Lite
    );
  }

  private updateLicensePackagesByCategory(categoryCode: AccountCategoryCode) {
    this._licensePackages = this._licensePackages?.map((licensePackage) => ({
      ...licensePackage,
      disabled: this.preventCreatingAccountWithLicensePackage(
        categoryCode,
        licensePackage.code,
      ),
    }));

    if (categoryCode === AccountCategoryCode.Trial) {
      this.licensePackage = LicensePackageCode.Trial;
      this.packageSize.patchValue(TrialQuotas.AudienceProfiles);
      this.packageSize.setValidators([
        Validators.required,
        Validators.min(0),
        Validators.max(TrialQuotas.AudienceProfiles),
      ]);
    } else if (categoryCode === AccountCategoryCode.SuiteMaxo) {
      this.licensePackage = LicensePackageCode.Freemium;
      this.packageSize.patchValue(FreemiumQuotas.AudienceProfiles);
      this.packageSize.setValidators([
        Validators.required,
        Validators.min(0),
        Validators.max(9999999),
      ]);
    } else {
      this.licensePackage = LicensePackageCode.Automate;
      this.packageSize.patchValue(AutomateQuotas.AudienceProfiles);
      this.packageSize.setValidators([
        Validators.required,
        Validators.min(0),
        Validators.max(9999999),
      ]);
    }

    this.packageSize.updateValueAndValidity();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
